import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { installFlushOnHide, trackClick, trackScreen } from '@/lib/analytics';

/** Invisible component: records screen views on navigation and clicks globally.
 *  Clicks resolve their name from the nearest `data-track` ancestor (falling
 *  back to the tag name) and carry viewport coordinates for the heatmap. */
export function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    trackScreen(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    // Strip the Vite base (e.g. "/tg-bot") so click paths match the router-
    // relative paths that screen views are stored under — otherwise the
    // click-map per-screen filter never lines up on subpath deployments.
    const appPath = (): string => {
      const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
      const raw = window.location.pathname;
      return (base && raw.startsWith(base) ? raw.slice(base.length) : raw) || '/';
    };
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const tagged = target?.closest('[data-track]') as HTMLElement | null;
      const name = tagged?.getAttribute('data-track') || `click:${target?.tagName.toLowerCase() ?? 'el'}`;
      trackClick(name, appPath(), {
        x: Math.round(e.clientX),
        y: Math.round(e.clientY),
        vw: window.innerWidth,
        vh: window.innerHeight,
      });
    };
    // Bubble phase: never pre-empt the app's own click handlers.
    document.addEventListener('click', onClick);
    const uninstall = installFlushOnHide();
    return () => {
      document.removeEventListener('click', onClick);
      uninstall();
    };
  }, []);

  return null;
}
